import {
  useState,
  useMemo,
  useEffect,
  useCallback,
  Component,
  ErrorInfo,
  ReactNode,
} from "react";
import {
  View,
  Pressable,
  Alert,
  Platform,
  ActionSheetIOS,
  InteractionManager,
  Modal,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTheme } from "@/theme";
import { Card } from "@/components/ui/Card";
import { ThemedText, SubHeading, Body } from "@/components/ui/Text";
import { Button } from "@/components/ui/Button";
import {
  groupItineraryItemsByDate,
  realignTemplateItineraryToTripStart,
  updateItineraryItem,
} from "@/services/itinerary";
import type { ItineraryItem } from "@/services/itinerary";
import type { Trip } from "@/services/trips";
import type { Booking } from "@/services/bookings";
import {
  fetchOpenMeteoDailyWeather,
  fetchWeatherForecast,
  getDestinationCoordinates,
  type WeatherForecast,
} from "@/services/weather";
import { getHistoricalWeatherForTrip } from "@/services/historicalWeather";
import { getHistoricalWeather } from "@/services/historicalWeather";
import { getTimezoneForLocation } from "@/services/timezones";
import { coerceDate, fromLocalDateKey, toLocalDateKey } from "@/utils/dateKeys";
import { mapWeatherConditionToDayCondition } from "@/utils/dayWeather";
import { resolveTripWeatherLocation } from "@/utils/tripWeatherLocation";
import {
  DayWeatherSheet,
  type DayWeather,
  type WeatherAlert,
} from "./DayWeatherSheet";
import { CalendarItineraryView } from "./CalendarItineraryView";
import { VerticalFlowView } from "./VerticalFlowView";
import { SmartSchedulingSuggestions } from "./SmartSchedulingSuggestions";

// NOTE: These are imported statically (not React.lazy) to avoid split-bundle loading errors
// like: LoadBundleFromServerRequestError: Could not load bundle

// Error boundary for ItineraryView to catch initialization errors
class ItineraryViewErrorBoundary extends Component<
  {
    children: ReactNode;
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
  },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: {
    children: ReactNode;
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
  }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(
      "[ItineraryView] Error boundary caught error:",
      error,
      errorInfo,
    );
    this.props.onError?.(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <View style={{ padding: 16 }}>
          <ThemedText variant="bodyStrong" color="#ff6b6b">
            Error loading itinerary view
          </ThemedText>
          {this.state.error && (
            <Body color="#666" style={{ marginTop: 8 }}>
              {this.state.error.message}
            </Body>
          )}
        </View>
      );
    }

    return this.props.children;
  }
}

type ViewMode = "calendar" | "list";

type ItineraryViewProps = {
  trip: Trip;
  items: ItineraryItem[];
  focusedDateKey?: string;
  /** After template date repair writes to Firestore, parent should re-fetch so Plan updates without waiting on snapshot quirks. */
  refreshPlanItineraryFromServer?: () => Promise<void>;
  bookings?: Booking[];
  onAddItem?: (date?: Date) => void;
  onOpenImport?: () => void;
  onEditItem?: (item: ItineraryItem) => void;
  onDeleteItem?: (item: ItineraryItem) => void;
  onViewDocument?: (
    url: string,
    meta?: {
      offlinePath?: string | null;
      fileName?: string;
      contentType?: string | null;
    },
  ) => void;
  onOpenIdeas?: (
    preferredTab?: "ai" | "localPicks" | "amadeus" | "for_you",
    targetDate?: Date,
  ) => void;
  canEdit?: boolean;
  isArchived?: boolean;
  isTripActive?: boolean;
  onOpenMobility?: (itemId: string) => void;
};

type DailyWeatherBadge = {
  iconName: string;
  label: string;
  tempMax?: number;
  tempMin?: number;
  precipProb?: number;
  windSpeed?: number;
  condition?: string;
  description?: string;
  source?: "forecast" | "historical";
  alerts?: WeatherAlert[];
};

type DailyTimezoneBadge = {
  label: string;
  timezone?: string;
};

export function ItineraryView(props: ItineraryViewProps) {
  const {
    trip,
    items,
    focusedDateKey,
    refreshPlanItineraryFromServer,
    bookings = [],
    onAddItem,
    onOpenImport,
    onEditItem,
    onDeleteItem,
    onViewDocument,
    onOpenIdeas,
    canEdit = false,
    isArchived = false,
    isTripActive = false,
    onOpenMobility,
  } = props;

  const theme = useTheme();
  const [viewMode, setViewMode] = useState<ViewMode>("calendar");
  const [dailyWeather, setDailyWeather] = useState<
    Record<string, DailyWeatherBadge>
  >({});
  const [dailyTimezone, setDailyTimezone] = useState<
    Record<string, DailyTimezoneBadge>
  >({});
  const [weatherSheetVisible, setWeatherSheetVisible] = useState(false);
  const [weatherSheetDateKey, setWeatherSheetDateKey] = useState<string | null>(
    null,
  );
  const [dailyWeatherAlerts, setDailyWeatherAlerts] = useState<
    Record<string, WeatherAlert[]>
  >({});
  const [repairingDates, setRepairingDates] = useState(false);
  const [repairStatusMessage, setRepairStatusMessage] = useState<string | null>(
    null,
  );
  /** Web: Alert.alert with multiple buttons is unreliable; use an inline chooser when both add paths exist. */
  const [webAddChoiceVisible, setWebAddChoiceVisible] = useState(false);

  const safeItems = useMemo(() => (Array.isArray(items) ? items : []), [items]);
  const safeTripStartDate = useMemo(
    () => coerceDate(trip?.startDate),
    [trip?.startDate],
  );
  const safeTripEndDate = useMemo(
    () => coerceDate(trip?.endDate),
    [trip?.endDate],
  );

  useEffect(() => {
    if (!Array.isArray(items)) {
      console.error("[ItineraryView] items is not an array:", items);
    }
  }, [items]);

  useEffect(() => {
    if (!safeTripStartDate || !safeTripEndDate) {
      console.error("[ItineraryView] Invalid trip dates:", trip);
    }
  }, [safeTripStartDate, safeTripEndDate, trip]);

  const openAddMenu = useCallback(() => {
    const canImport = typeof onOpenImport === "function";
    const canManual = typeof onAddItem === "function";

    if (!canManual && !canImport) return;

    const handlePick = (picked: "manual" | "import" | "cancel") => {
      if (picked === "manual") onAddItem?.();
      if (picked === "import") onOpenImport?.();
    };

    if (Platform.OS === "ios") {
      const options = [
        ...(canManual ? ["Add manually"] : []),
        ...(canImport ? ["Import & connect"] : []),
        "Cancel",
      ];
      const cancelButtonIndex = options.length - 1;

      ActionSheetIOS.showActionSheetWithOptions(
        {
          title: "Add to itinerary",
          options,
          cancelButtonIndex,
        },
        (buttonIndex) => {
          if (buttonIndex === cancelButtonIndex) return handlePick("cancel");

          // Map selection back to action considering optional entries
          const manualIndex = canManual ? 0 : -1;
          const importIndex = canManual ? 1 : 0;

          if (canManual && buttonIndex === manualIndex)
            return handlePick("manual");
          if (canImport && buttonIndex === importIndex)
            return handlePick("import");
          return handlePick("cancel");
        },
      );
      return;
    }

    if (Platform.OS === "web") {
      if (canManual && !canImport) {
        onAddItem?.();
        return;
      }
      if (!canManual && canImport) {
        onOpenImport?.();
        return;
      }
      if (canManual && canImport) {
        setWebAddChoiceVisible(true);
        return;
      }
      return;
    }

    // Android fallback
    const buttons: Array<{
      text: string;
      onPress?: () => void;
      style?: "cancel";
    }> = [];
    if (canManual)
      buttons.push({
        text: "Add manually",
        onPress: () => handlePick("manual"),
      });
    if (canImport)
      buttons.push({
        text: "Import & connect",
        onPress: () => handlePick("import"),
      });
    buttons.push({ text: "Cancel", style: "cancel" });
    Alert.alert(
      "Add to itinerary",
      "Choose how you want to add items",
      buttons,
    );
  }, [onAddItem, onOpenImport]);

  // Safely compute groupedByDate with error handling
  const groupedByDate = useMemo(() => {
    try {
      // Validate items before processing
      if (!safeItems || safeItems.length === 0) {
        return new Map();
      }

      // Check for invalid items
      const validItems = safeItems.filter((item) => {
        if (!item || !item.startDate) {
          return false;
        }
        // Ensure startDate is a Date object
        if (!(item.startDate instanceof Date)) {
          // Try to convert it if it's a string or has a toDate method
          if (typeof item.startDate === "string") {
            try {
              const converted = new Date(item.startDate);
              if (!isNaN(converted.getTime())) {
                // Update the item in place (this is a workaround for data issues)
                (item as any).startDate = converted;
                return true;
              }
            } catch (error) {
              console.error(
                "[ItineraryView] Error converting string date:",
                error,
              );
            }
          } else if (
            item.startDate &&
            typeof item.startDate === "object" &&
            "toDate" in item.startDate
          ) {
            try {
              const converted = (item.startDate as any).toDate();
              if (converted instanceof Date && !isNaN(converted.getTime())) {
                (item as any).startDate = converted;
                return true;
              }
            } catch (error) {
              console.error("[ItineraryView] Error calling toDate():", error);
            }
          }
          return false;
        }
        // Validate the date is not invalid
        if (isNaN(item.startDate.getTime())) {
          return false;
        }
        return true;
      });

      return groupItineraryItemsByDate(validItems);
    } catch (error) {
      console.error("[ItineraryView] Error computing groupedByDate:", error);
      return new Map(); // Return empty map on error
    }
  }, [safeItems]);

  const tripStartKey = useMemo(
    () => (safeTripStartDate ? toLocalDateKey(safeTripStartDate) : null),
    [safeTripStartDate],
  );
  const tripEndKey = useMemo(
    () => (safeTripEndDate ? toLocalDateKey(safeTripEndDate) : null),
    [safeTripEndDate],
  );

  const hasAnyInRange = useMemo(() => {
    if (!safeItems?.length) return true;
    if (!tripStartKey || !tripEndKey) return true;
    for (const it of safeItems) {
      const d =
        it.startDate instanceof Date ? it.startDate : coerceDate(it.startDate);
      if (!(d instanceof Date) || isNaN(d.getTime())) continue;
      const key = toLocalDateKey(d);
      if (key >= tripStartKey && key <= tripEndKey) return true;
    }
    return false;
  }, [safeItems, tripStartKey, tripEndKey]);

  const hasTemplateItems = useMemo(
    () => safeItems.some((it) => (it as any)?.source === "template"),
    [safeItems],
  );

  const shouldOfferRepair =
    safeItems.length > 0 &&
    !hasAnyInRange &&
    hasTemplateItems &&
    !isArchived &&
    canEdit;

  const selectedDayWeather = useMemo<DayWeather | undefined>(() => {
    if (!weatherSheetDateKey) return undefined;
    return {
      ...(dailyWeather[weatherSheetDateKey] as DayWeather | undefined),
      alerts: dailyWeatherAlerts[weatherSheetDateKey],
    } as DayWeather;
  }, [dailyWeather, dailyWeatherAlerts, weatherSheetDateKey]);

  const handleRepairDates = useCallback(async () => {
    if (!trip?.id || !safeTripStartDate) return;
    setRepairStatusMessage(null);
    setRepairingDates(true);
    const REPAIR_TIMEOUT_MS = 45_000;
    try {
      const result = await new Promise<
        Awaited<ReturnType<typeof realignTemplateItineraryToTripStart>>
      >((resolve, reject) => {
        const timer = setTimeout(
          () => reject(new Error("repair-timeout")),
          REPAIR_TIMEOUT_MS,
        );
        void realignTemplateItineraryToTripStart(trip.id, safeTripStartDate)
          .then((r) => {
            clearTimeout(timer);
            resolve(r);
          })
          .catch((e) => {
            clearTimeout(timer);
            reject(e);
          });
      });

      // Never block UI teardown on server refresh — Firestore `fromServer` reads can stall until the app
      // backgrounds/resumes on flaky networks, leaving "Fixing…" stuck (see Plan tab resync).
      void refreshPlanItineraryFromServer?.().catch(() => {
        /* best-effort: subscription may still converge */
      });

      InteractionManager.runAfterInteractions(() => {
        requestAnimationFrame(() => {
          if (result.updated > 0) {
            setRepairStatusMessage(
              `Moved ${result.updated} template-based item${result.updated === 1 ? "" : "s"}; syncing in the background.`,
            );
            Alert.alert(
              "Itinerary dates updated",
              `Moved ${result.updated} template-based item${result.updated === 1 ? "" : "s"} to align with your trip start date.`,
            );
          } else if (result.deltaDays === 0) {
            setRepairStatusMessage(
              "Template dates match your trip start, but some rows may still sit outside the trip window until refresh.",
            );
            Alert.alert(
              "Already aligned",
              "Template-based itinerary anchors already match your trip start date. If the warning persists, pull to refresh — booking-linked dates are left unchanged.",
            );
          } else {
            setRepairStatusMessage(
              "No template-based rows were moved. Pull to refresh if this banner remains.",
            );
            Alert.alert(
              "Nothing to move",
              "We didn’t find template-sourced items to shift. If this message still shows, pull to refresh on Plan.",
            );
          }
        });
      });
    } catch (error) {
      const timedOut = error instanceof Error && error.message === "repair-timeout";
      if (timedOut) {
        setRepairStatusMessage("Fix timed out — check your connection and try again.");
        InteractionManager.runAfterInteractions(() => {
          requestAnimationFrame(() => {
            Alert.alert(
              "Could not finish",
              "Fixing dates took too long (network may be slow or offline). Please try again.",
            );
          });
        });
      } else {
        InteractionManager.runAfterInteractions(() => {
          requestAnimationFrame(() => {
            Alert.alert("Could not fix dates", "Please try again.");
          });
        });
      }
    } finally {
      setRepairingDates(false);
    }
  }, [trip?.id, safeTripStartDate, refreshPlanItineraryFromServer]);

  const handleApplySuggestion = async (
    itemId: string,
    startTime: string,
    endTime?: string,
  ) => {
    try {
      const item = safeItems.find((i) => i.id === itemId);
      if (!item) return;

      await updateItineraryItem(trip.id, itemId, {
        startTime,
        endTime,
      });

      // Optionally refresh or show success message
    } catch (error) {
      console.error("Error applying suggestion:", error);
    }
  };

  // IMPORTANT: use an actual location string for weather lookup (avoid trip.name which may include extra text).
  const destinationLabel = useMemo(() => {
    // Prefer the first city when available (country-only destinations like "Turkey" geocode poorly for temps).
    return trip.destinations?.[0]?.city || trip.destination || "";
  }, [trip.destination, trip.destinations]);

  const destinationCoordinates = useMemo(() => {
    if (trip.destinationCoordinates) return trip.destinationCoordinates;
    const destWithCoords = trip.destinations?.find((d: any) => d?.coordinates);
    return destWithCoords?.coordinates;
  }, [trip.destinationCoordinates, trip.destinations]);

  const isValidCoordinates = (
    coords?: { latitude?: number; longitude?: number } | null,
  ) => {
    if (
      !coords ||
      coords.latitude === undefined ||
      coords.longitude === undefined
    )
      return false;
    const { latitude, longitude } = coords;
    return (
      Number.isFinite(latitude) &&
      Number.isFinite(longitude) &&
      Math.abs(latitude) <= 90 &&
      Math.abs(longitude) <= 180 &&
      !(latitude === 0 && longitude === 0)
    );
  };

  const mapOpenMeteoDailyToBadge = (
    day: Awaited<ReturnType<typeof fetchOpenMeteoDailyWeather>>[number],
  ): DailyWeatherBadge => {
    const condition = mapWeatherConditionToDayCondition(day.condition);
    let iconName: DailyWeatherBadge["iconName"] = "partly-sunny-outline";
    if (condition === "sunny") iconName = "sunny-outline";
    else if (condition === "partly_cloudy") iconName = "partly-sunny-outline";
    else if (condition === "cloudy") iconName = "cloud-outline";
    else if (condition === "rainy") iconName = "rainy-outline";
    else if (condition === "stormy") iconName = "thunderstorm-outline";
    else if (condition === "snowy") iconName = "snow-outline";

    return {
      iconName,
      label: `${day.tempHigh}°`,
      tempMin: day.tempLow,
      tempMax: day.tempHigh,
      condition: day.condition,
      description: day.description,
      source: "forecast",
    };
  };

  const mapForecastToBadge = (
    day: WeatherForecast["forecast"][number],
  ): DailyWeatherBadge => {
    const code = (day.icon || "").slice(0, 2);
    let iconName = "partly-sunny-outline";

    if (code === "01") iconName = "sunny-outline";
    else if (code === "02") iconName = "partly-sunny-outline";
    else if (code === "03" || code === "04") iconName = "cloud-outline";
    else if (code === "09" || code === "10") iconName = "rainy-outline";
    else if (code === "11") iconName = "thunderstorm-outline";
    else if (code === "13") iconName = "snow-outline";
    else if (code === "50") iconName = "cloudy-outline";

    const tempMax = Number.isFinite(day.temperature?.max)
      ? Math.round(day.temperature.max)
      : undefined;
    const tempMin = Number.isFinite(day.temperature?.min)
      ? Math.round(day.temperature.min)
      : undefined;
    const precipProb = Number.isFinite(day.precipitation?.probability)
      ? day.precipitation.probability
      : undefined;

    return {
      iconName,
      label:
        typeof tempMax === "number"
          ? `${tempMax}°`
          : day.condition || "Forecast",
      tempMin,
      tempMax,
      precipProb,
      windSpeed: Number.isFinite(day.windSpeed) ? day.windSpeed : undefined,
      condition: day.condition,
      description: day.description,
      source: "forecast",
    };
  };

  const severityRank: Record<WeatherAlert["severity"], number> = {
    critical: 4,
    high: 3,
    medium: 2,
    low: 1,
  };

  const addAlertToDay = (
    map: Record<string, WeatherAlert[]>,
    dateKey: string,
    alert: WeatherAlert,
  ) => {
    if (!map[dateKey]) map[dateKey] = [];
    map[dateKey].push(alert);
    // Keep highest severity first
    map[dateKey].sort(
      (a, b) => severityRank[b.severity] - severityRank[a.severity],
    );
  };

  const applyForecastAlertsToDays = (
    forecast: WeatherForecast,
    segmentStart: Date,
    segmentEnd: Date,
    map: Record<string, WeatherAlert[]>,
  ) => {
    const alerts = forecast.alerts || [];
    if (!alerts.length) return;

    const segKeys = new Set(buildTripDateKeys(segmentStart, segmentEnd));
    for (const a of alerts) {
      const start = new Date(a.start);
      const end = new Date(a.end);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) continue;

      // Walk each day in the alert window and apply within this segment.
      const cursor = new Date(start);
      cursor.setHours(0, 0, 0, 0);
      const endLocal = new Date(end);
      endLocal.setHours(0, 0, 0, 0);
      while (cursor <= endLocal) {
        const key = toLocalDateKey(cursor);
        if (segKeys.has(key)) {
          addAlertToDay(map, key, {
            severity: a.severity,
            title: a.title,
            description: a.description,
          });
        }
        cursor.setDate(cursor.getDate() + 1);
      }
    }
  };

  const buildTripDateKeys = (start: Date, end: Date): string[] => {
    const keys: string[] = [];
    const cursor = new Date(start);
    cursor.setHours(0, 0, 0, 0);
    const endLocal = new Date(end);
    endLocal.setHours(0, 0, 0, 0);
    while (cursor <= endLocal) {
      keys.push(toLocalDateKey(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return keys;
  };

  const buildDailyTimezoneBadges = (
    start: Date,
    end: Date,
    timezone: string,
  ): Record<string, DailyTimezoneBadge> => {
    const keys = buildTripDateKeys(start, end);

    const getLabelForDate = (date: Date): string => {
      if (!timezone) return "";
      if (timezone.startsWith("UTC")) return timezone.replace("UTC", "GMT");

      try {
        const fmt = new Intl.DateTimeFormat("en-US", {
          timeZone: timezone,
          timeZoneName: "short",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });
        const parts = fmt.formatToParts(date);
        const tzName = parts.find((p) => p.type === "timeZoneName")?.value;
        if (tzName) {
          return tzName.replace("UTC", "GMT");
        }
      } catch {
        // ignore
      }

      return timezone.includes("/")
        ? timezone.split("/")[1]?.replace(/_/g, " ") || timezone
        : timezone;
    };

    return keys.reduce(
      (acc, key) => {
        const [y, m, d] = key.split("-").map((v) => parseInt(v, 10));
        const date = new Date(y, (m || 1) - 1, d || 1);
        acc[key] = { label: getLabelForDate(date), timezone };
        return acc;
      },
      {} as Record<string, DailyTimezoneBadge>,
    );
  };

  const buildTimezoneBadgesByDestination = useCallback(
    async (
      startDate: Date,
      endDate: Date,
    ): Promise<Record<string, DailyTimezoneBadge>> => {
      const destinations = Array.isArray(trip.destinations)
        ? trip.destinations
        : [];
      if (!destinations.length) return {};

      const byDate: Record<string, DailyTimezoneBadge> = {};
      for (const dest of destinations) {
        try {
          const destStart =
            dest.startDate instanceof Date
              ? new Date(dest.startDate)
              : new Date(dest.startDate as any);
          const destEnd =
            dest.endDate instanceof Date
              ? new Date(dest.endDate)
              : new Date(dest.endDate as any);
          if (isNaN(destStart.getTime()) || isNaN(destEnd.getTime())) continue;

          const segStart =
            destStart < startDate ? new Date(startDate) : destStart;
          const segEnd = destEnd > endDate ? new Date(endDate) : destEnd;

          const coords =
            (dest.coordinates && isValidCoordinates(dest.coordinates)
              ? {
                  latitude: dest.coordinates.latitude,
                  longitude: dest.coordinates.longitude,
                }
              : null) ||
            (await getDestinationCoordinates(
              dest.country ? `${dest.city}, ${dest.country}` : dest.city,
            )) ||
            null;

          if (!coords || !isValidCoordinates(coords)) continue;

          const tz = await getTimezoneForLocation({
            latitude: coords.latitude,
            longitude: coords.longitude,
          });
          if (!tz) continue;

          Object.assign(byDate, buildDailyTimezoneBadges(segStart, segEnd, tz));
        } catch {
          // ignore segment failure and keep building others
        }
      }

      return byDate;
    },
    [trip.destinations],
  );

  const mapHistoricalToBadges = (
    start: Date,
    end: Date,
    historical: {
      avgTemp: number;
      minTemp: number;
      maxTemp: number;
      avgPrecipitation: number;
      rainyDays: number;
    },
  ): Record<string, DailyWeatherBadge> => {
    const keys = buildTripDateKeys(start, end);
    const isRainy =
      historical.rainyDays >= 10 || historical.avgPrecipitation >= 70;
    const iconName = isRainy
      ? "rainy-outline"
      : historical.maxTemp <= 5
        ? "snow-outline"
        : historical.maxTemp >= 25
          ? "sunny-outline"
          : "partly-sunny-outline";
    const label = `${Math.round(historical.maxTemp)}°`;
    return keys.reduce(
      (acc, key) => {
        acc[key] = {
          iconName,
          label,
          tempMin: Math.round(historical.minTemp),
          tempMax: Math.round(historical.maxTemp),
          precipProb: isRainy ? 60 : 20,
          condition: isRainy ? "Likely rain" : "Typical conditions",
          description: "Based on historical averages",
          source: "historical",
        };
        return acc;
      },
      {} as Record<string, DailyWeatherBadge>,
    );
  };

  const buildHistoricalBadgesByDestination = useCallback(
    async (
      startDate: Date,
      endDate: Date,
      coordsFallback: {
        latitude: number;
        longitude: number;
        city: string;
        country: string;
      },
    ): Promise<Record<string, DailyWeatherBadge>> => {
      const destinations = Array.isArray(trip.destinations)
        ? trip.destinations
        : [];
      if (!destinations.length) {
        const historical = await getHistoricalWeatherForTrip(
          {
            startDate,
            endDate,
            destination: trip.destination,
            destinations: trip.destinations,
          },
          coordsFallback,
        );
        return historical
          ? mapHistoricalToBadges(startDate, endDate, historical)
          : {};
      }

      const byDate: Record<string, DailyWeatherBadge> = {};
      for (const dest of destinations) {
        try {
          const destStart =
            dest.startDate instanceof Date
              ? new Date(dest.startDate)
              : new Date(dest.startDate as any);
          const destEnd =
            dest.endDate instanceof Date
              ? new Date(dest.endDate)
              : new Date(dest.endDate as any);
          if (isNaN(destStart.getTime()) || isNaN(destEnd.getTime())) continue;

          const month = destStart.getMonth() + 1;
          const segmentLocation = await resolveTripWeatherLocation(trip, destStart);
          const coords = segmentLocation
            ? {
                latitude: segmentLocation.latitude,
                longitude: segmentLocation.longitude,
                city: segmentLocation.city,
                country: segmentLocation.countryCode || "",
              }
            : coordsFallback;

          const historical = await getHistoricalWeather(
            coords.latitude,
            coords.longitude,
            coords.city,
            coords.country,
            month,
          );
          if (!historical) continue;

          const segmentBadges = mapHistoricalToBadges(
            destStart,
            destEnd,
            historical,
          );
          Object.assign(byDate, segmentBadges);
        } catch {
          // ignore segment failure and keep building others
        }
      }

      if (!Object.keys(byDate).length) {
        const historical = await getHistoricalWeatherForTrip(
          {
            startDate,
            endDate,
            destination: trip.destination,
            destinations: trip.destinations,
          },
          coordsFallback,
        );
        return historical
          ? mapHistoricalToBadges(startDate, endDate, historical)
          : {};
      }

      return byDate;
    },
    [trip],
  );

  const buildForecastBadgesByDestination = useCallback(
    async (
      startDate: Date,
      endDate: Date,
      maxDays: number,
    ): Promise<{
      badges: Record<string, DailyWeatherBadge>;
      alerts: Record<string, WeatherAlert[]>;
    }> => {
      const destinations = Array.isArray(trip.destinations)
        ? trip.destinations
        : [];
      if (!destinations.length) return { badges: {}, alerts: {} };

      const byDate: Record<string, DailyWeatherBadge> = {};
      const alertMap: Record<string, WeatherAlert[]> = {};
      for (const dest of destinations) {
        try {
          const destStart =
            dest.startDate instanceof Date
              ? new Date(dest.startDate)
              : new Date(dest.startDate as any);
          const destEnd =
            dest.endDate instanceof Date
              ? new Date(dest.endDate)
              : new Date(dest.endDate as any);
          if (isNaN(destStart.getTime()) || isNaN(destEnd.getTime())) continue;

          // Bound segment within trip range.
          const segStart =
            destStart < startDate ? new Date(startDate) : destStart;
          const segEnd = destEnd > endDate ? new Date(endDate) : destEnd;

          const segmentDays = Math.min(
            maxDays,
            Math.max(
              1,
              Math.ceil(
                (segEnd.getTime() - segStart.getTime()) / (1000 * 60 * 60 * 24),
              ) + 1,
            ),
          );

          const coords =
            (dest.coordinates && isValidCoordinates(dest.coordinates)
              ? {
                  latitude: dest.coordinates.latitude,
                  longitude: dest.coordinates.longitude,
                  city: dest.city,
                  country: dest.country || "",
                }
              : null) ||
            (await getDestinationCoordinates(
              dest.country ? `${dest.city}, ${dest.country}` : dest.city,
            )) ||
            null;

          if (!coords || !isValidCoordinates(coords)) continue;

          const forecast = await fetchWeatherForecast({
            latitude: coords.latitude,
            longitude: coords.longitude,
            city: coords.city,
            countryCode: coords.country || dest.country || undefined,
            days: segmentDays,
            targetDate: toLocalDateKey(segStart),
          });
          if (!forecast?.forecast?.length) continue;

          // Only keep days inside the segment.
          const segmentKeys = new Set(buildTripDateKeys(segStart, segEnd));
          for (const day of forecast.forecast) {
            const key = (day.date || "").slice(0, 10);
            if (!key || !segmentKeys.has(key)) continue;
            byDate[key] = mapForecastToBadge(day);
          }

          applyForecastAlertsToDays(forecast, segStart, segEnd, alertMap);
        } catch {
          // ignore segment failure and keep building others
        }
      }

      return { badges: byDate, alerts: alertMap };
    },
    [trip.destinations],
  );

  // Fetch forecast (best-effort). Weather service is cached, so re-fetching on relevant changes is safe.
  useEffect(() => {
    if (!trip?.id || !safeTripStartDate || !safeTripEndDate) return;

    let cancelled = false;

    const load = async () => {
      try {
        const startDate = new Date(safeTripStartDate);
        const endDate = new Date(safeTripEndDate);
        const days =
          Math.ceil(
            (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
          ) + 1;
        const daysUntilTrip = Math.ceil(
          (startDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24),
        );

        const weatherLocation = await resolveTripWeatherLocation(trip);
        const coords = weatherLocation
          ? {
              latitude: weatherLocation.latitude,
              longitude: weatherLocation.longitude,
              city: weatherLocation.city || destinationLabel || trip.destination || "",
              country: weatherLocation.countryCode || "",
            }
          : (isValidCoordinates(destinationCoordinates)
              ? {
                  latitude: destinationCoordinates!.latitude,
                  longitude: destinationCoordinates!.longitude,
                  city:
                    destinationLabel ||
                    trip.destinations?.[0]?.city ||
                    trip.destination ||
                    "",
                }
              : null) ||
            (destinationLabel
              ? await getDestinationCoordinates(destinationLabel)
              : null);

        if (__DEV__ && weatherLocation) {
          console.log("[ItineraryView] Weather location", {
            tripId: trip.id,
            label: weatherLocation.label,
            latitude: weatherLocation.latitude,
            longitude: weatherLocation.longitude,
            source: weatherLocation.source,
          });
        }

        if (!coords || !isValidCoordinates(coords)) {
          if (!cancelled) {
            setDailyWeather({});
            setDailyTimezone({});
            setDailyWeatherAlerts({});
          }
          return;
        }

        // Timezone (best-effort). Used for day headers to clarify local time context.
        const perStopTz = await buildTimezoneBadgesByDestination(
          startDate,
          endDate,
        );
        if (!cancelled && Object.keys(perStopTz).length > 0) {
          setDailyTimezone(perStopTz);
        } else {
          const timezone = await getTimezoneForLocation({
            latitude: coords.latitude,
            longitude: coords.longitude,
          });
          if (!cancelled) {
            setDailyTimezone(
              timezone
                ? buildDailyTimezoneBadges(startDate, endDate, timezone)
                : {},
            );
          }
        }

        const maxForecastDays = 16;
        const tripKeys = buildTripDateKeys(startDate, endDate);
        const coordsPayload = {
          latitude: coords.latitude,
          longitude: coords.longitude,
          city: coords.city || destinationLabel,
          country: (coords as any).country || "",
        };

        // If the trip is beyond the forecast horizon, use historical averages so badges still appear.
        if (daysUntilTrip > maxForecastDays) {
          const historicalBadges = await buildHistoricalBadgesByDestination(
            startDate,
            endDate,
            coordsPayload,
          );
          if (!cancelled) {
            setDailyWeather(historicalBadges);
            setDailyWeatherAlerts({});
          }
          return;
        }

        // Prefer Open-Meteo daily (same source as Weather QA) before other forecast paths.
        let byDate: Record<string, DailyWeatherBadge> = {};
        const openMeteoDays = await fetchOpenMeteoDailyWeather(
          coords.latitude,
          coords.longitude,
          toLocalDateKey(startDate),
          Math.min(Math.max(days, 1), maxForecastDays),
        );
        openMeteoDays.forEach((day) => {
          if (tripKeys.includes(day.dateKey)) {
            byDate[day.dateKey] = mapOpenMeteoDailyToBadge(day);
          }
        });

        // Build per-stop forecast if we have dated destinations; otherwise use a single-location forecast.
        const perStopForecast = await buildForecastBadgesByDestination(
          startDate,
          endDate,
          maxForecastDays,
        );

        byDate = { ...byDate, ...perStopForecast.badges };
        let alertMap: Record<string, WeatherAlert[]> = perStopForecast.alerts;
        const overlapsPerStop = tripKeys.some((k) => Boolean(byDate[k]));

        if (!overlapsPerStop) {
          const forecast = await fetchWeatherForecast({
            latitude: coords.latitude,
            longitude: coords.longitude,
            city: coords.city || destinationLabel || undefined,
            days: Math.min(Math.max(days, 1), maxForecastDays),
            targetDate: toLocalDateKey(startDate),
          });

          if (forecast?.forecast?.length) {
            const single: Record<string, DailyWeatherBadge> = {};
            forecast.forecast.forEach((day) => {
              const key = (day.date || "").slice(0, 10);
              if (key) single[key] = mapForecastToBadge(day);
            });
            byDate = { ...byDate, ...single };

            const alertsOnly: Record<string, WeatherAlert[]> = {};
            applyForecastAlertsToDays(forecast, startDate, endDate, alertsOnly);
            alertMap = { ...alertMap, ...alertsOnly };
          }
        }

        const overlaps = tripKeys.some((k) => Boolean(byDate[k]));
        if (!overlaps) {
          if (!cancelled) {
            const historicalBadges = await buildHistoricalBadgesByDestination(
              startDate,
              endDate,
              coordsPayload,
            );
            setDailyWeather(historicalBadges);
          }
          return;
        }

        const missingKeys = tripKeys.filter((k) => !byDate[k]);
        if (!cancelled) {
          if (missingKeys.length > 0) {
            const historicalBadges = await buildHistoricalBadgesByDestination(
              startDate,
              endDate,
              coordsPayload,
            );
            const filled: Record<string, DailyWeatherBadge> = { ...historicalBadges };
            tripKeys.forEach((key) => {
              if (byDate[key]) {
                filled[key] = byDate[key];
              }
            });
            setDailyWeather(filled);
          } else {
            setDailyWeather(byDate);
          }
          setDailyWeatherAlerts(alertMap);
        }
      } catch {
        if (!cancelled) {
          setDailyWeather({});
          setDailyWeatherAlerts({});
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    trip?.id,
    safeTripStartDate,
    safeTripEndDate,
    destinationLabel,
    destinationCoordinates?.latitude,
    destinationCoordinates?.longitude,
    buildForecastBadgesByDestination,
    buildTimezoneBadgesByDestination,
  ]);

  if (!safeTripStartDate || !safeTripEndDate) {
    return null;
  }

  return (
    <>
      <View style={{ flex: 1 }}>
        {/* Itinerary Container - Unified View */}
        <Card
          variant="elevated"
          elevation={2}
          style={{
            marginBottom: theme.spacing(2),
            overflow: "hidden",
          }}
        >
          {/* Itinerary Header with Actions */}
          <View style={{ marginBottom: theme.spacing(1.5) }}>
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: theme.spacing(1),
              }}
            >
              <View style={{ flex: 1 }}>
                <SubHeading style={{ fontSize: 18, fontWeight: "700" }}>
                  Itinerary
                </SubHeading>
                <Body
                  color={theme.colors.textSecondary}
                  style={{ fontSize: 12 }}
                >
                  {safeItems.length} {safeItems.length === 1 ? "item" : "items"}{" "}
                  across your trip
                </Body>
              </View>

              {/* View Toggle */}
              <View
                accessibilityLabel="Itinerary view"
                style={{
                  flexDirection: "row",
                  backgroundColor: theme.colors.borderMuted + "40",
                  borderRadius: theme.radii.md,
                  padding: 2,
                  gap: 2,
                }}
              >
                <Pressable
                  onPress={() => setViewMode("calendar")}
                  style={({ pressed }) => [
                    {
                      minWidth: 36,
                      minHeight: 36,
                      paddingHorizontal: theme.spacing(1),
                      paddingVertical: theme.spacing(0.5),
                      borderRadius: theme.radii.sm,
                      backgroundColor:
                        viewMode === "calendar"
                          ? theme.colors.primary
                          : "transparent",
                      opacity: pressed ? 0.7 : 1,
                      alignItems: "center",
                      justifyContent: "center",
                    },
                  ]}
                  accessibilityRole={
                    Platform.OS === "android" ? "button" : "tab"
                  }
                  accessibilityLabel="Calendar view"
                  accessibilityHint="Shows your itinerary grouped by day with a date selector"
                  accessibilityState={{ selected: viewMode === "calendar" }}
                >
                  <Ionicons
                    name="calendar-outline"
                    size={16}
                    color={
                      viewMode === "calendar"
                        ? theme.colors.surface
                        : theme.colors.textSecondary
                    }
                    accessible={false}
                  />
                </Pressable>
                <Pressable
                  onPress={() => setViewMode("list")}
                  style={({ pressed }) => [
                    {
                      minWidth: 36,
                      minHeight: 36,
                      paddingHorizontal: theme.spacing(1),
                      paddingVertical: theme.spacing(0.5),
                      borderRadius: theme.radii.sm,
                      backgroundColor:
                        viewMode === "list"
                          ? theme.colors.primary
                          : "transparent",
                      opacity: pressed ? 0.7 : 1,
                      alignItems: "center",
                      justifyContent: "center",
                    },
                  ]}
                  accessibilityRole={
                    Platform.OS === "android" ? "button" : "tab"
                  }
                  accessibilityLabel="List view"
                  accessibilityHint="Shows your itinerary as a chronological list"
                  accessibilityState={{ selected: viewMode === "list" }}
                >
                  <Ionicons
                    name="list-outline"
                    size={16}
                    color={
                      viewMode === "list"
                        ? theme.colors.surface
                        : theme.colors.textSecondary
                    }
                    accessible={false}
                  />
                </Pressable>
              </View>
            </View>

            {/* Inline Action Buttons (merged from Planning Actions) */}
            {!isArchived && canEdit && (
              <View
                style={{
                  flexDirection: "row",
                  gap: theme.spacing(1),
                  marginTop: theme.spacing(0.5),
                }}
              >
                {(onAddItem || onOpenImport) && (
                  <Pressable
                    onPress={openAddMenu}
                    style={({ pressed }) => ({
                      flex: 1,
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: theme.spacing(0.5),
                      paddingVertical: theme.spacing(1),
                      paddingHorizontal: theme.spacing(1.5),
                      borderRadius: theme.radii.pill,
                      backgroundColor: theme.colors.primary,
                      opacity: pressed ? 0.7 : 1,
                    })}
                    accessibilityRole="button"
                    accessibilityLabel="Add to itinerary"
                  >
                    <Ionicons
                      name="add"
                      size={16}
                      color={
                        theme.colorScheme === "dark" ? "#000000" : "#FFFFFF"
                      }
                    />
                    <ThemedText
                      variant="bodyStrong"
                      style={{
                        fontSize: 13,
                        fontWeight: "600",
                        color:
                          theme.colorScheme === "dark" ? "#000000" : "#FFFFFF",
                      }}
                    >
                      Add
                    </ThemedText>
                  </Pressable>
                )}
                {onOpenIdeas && (
                  <Pressable
                    onPress={() => onOpenIdeas()}
                    style={({ pressed }) => ({
                      flex: 1,
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: theme.spacing(0.5),
                      paddingVertical: theme.spacing(1),
                      paddingHorizontal: theme.spacing(1.5),
                      borderRadius: theme.radii.pill,
                      borderWidth: 1,
                      borderColor: theme.colors.primary,
                      backgroundColor: theme.colors.primary + "10",
                      opacity: pressed ? 0.7 : 1,
                    })}
                    accessibilityRole="button"
                    accessibilityLabel="Browse trip ideas"
                  >
                    <Ionicons
                      name="sparkles"
                      size={16}
                      color={theme.colors.primary}
                    />
                    <ThemedText
                      variant="bodyStrong"
                      style={{
                        fontSize: 13,
                        fontWeight: "600",
                        color: theme.colors.primary,
                      }}
                    >
                      Ideas
                    </ThemedText>
                  </Pressable>
                )}
              </View>
            )}
          </View>

          {shouldOfferRepair && (
            <View style={{ marginBottom: theme.spacing(2) }}>
              <Card
                variant="outlined"
                style={{
                  borderColor: theme.colors.warning + "60",
                  backgroundColor: theme.colors.warning + "10",
                }}
              >
                <View
                  style={{ padding: theme.spacing(1.5), gap: theme.spacing(1) }}
                >
                  <ThemedText variant="bodyStrong">
                    Itinerary dates look out of sync with this trip
                  </ThemedText>
                  <Body color={theme.colors.textSecondary}>
                    We found itinerary items, but none fall within{" "}
                    {safeTripStartDate.toLocaleDateString()}–
                    {safeTripEndDate.toLocaleDateString()}. This often happens after
                    trip dates change, or when items were added from trip templates or Ideas
                    (those count as template-based rows).
                  </Body>
                  <Button
                    label={repairingDates ? "Fixing…" : "Fix itinerary dates"}
                    variant="secondary"
                    disabled={repairingDates}
                    onPress={handleRepairDates}
                  />
                  {repairStatusMessage ? (
                    <Body color={theme.colors.textSecondary}>
                      {repairStatusMessage}
                    </Body>
                  ) : null}
                </View>
              </Card>
            </View>
          )}

          {/* Content - Inside the same container */}
          <View>
            {safeItems.length === 0 ? (
              <View
                style={{
                  alignItems: "center",
                  paddingVertical: theme.spacing(6),
                  gap: theme.spacing(2),
                }}
              >
                <View
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: 40,
                    backgroundColor: theme.colors.primary + "10",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: theme.spacing(1),
                  }}
                >
                  <Ionicons
                    name="calendar-outline"
                    size={40}
                    color={theme.colors.primary}
                  />
                </View>
                <ThemedText
                  variant="h3"
                  style={{
                    fontSize: 20,
                    fontWeight: "700",
                    textAlign: "center",
                  }}
                >
                  Start planning your trip
                </ThemedText>
                <Body
                  color={theme.colors.textSecondary}
                  style={{
                    textAlign: "center",
                    fontSize: 15,
                    lineHeight: 22,
                    maxWidth: 280,
                  }}
                >
                  {!isArchived && canEdit
                    ? "Add items manually or let AI create a personalized itinerary for you."
                    : "Itinerary items will appear here once added."}
                </Body>
                {!isArchived && canEdit && onAddItem && (
                  <View
                    style={{
                      gap: theme.spacing(1.5),
                      width: "100%",
                      maxWidth: 280,
                      marginTop: theme.spacing(1),
                    }}
                  >
                    <Button
                      label="Add Your First Item"
                      variant="primary"
                      onPress={() => onAddItem()}
                      icon={
                        <Ionicons
                          name="add"
                          size={18}
                          color={
                            theme.colorScheme === "dark" ? "#000000" : "#FFFFFF"
                          }
                        />
                      }
                      accessibilityLabel="Add your first itinerary item"
                      accessibilityHint="Opens a form to add a new activity, flight, or accommodation to your trip"
                    />
                  </View>
                )}
              </View>
            ) : (
              <>
                {viewMode === "calendar" ? (
                  <ItineraryViewErrorBoundary
                    onError={() => {
                      setViewMode("list");
                    }}
                    fallback={
                      <VerticalFlowView
                        items={safeItems}
                        bookings={bookings}
                        preferredTravelMode={trip.preferredTravelMode}
                        onEditItem={onEditItem}
                        onDeleteItem={onDeleteItem}
                        onViewDocument={onViewDocument}
                        canEdit={canEdit}
                        startDate={safeTripStartDate}
                        endDate={safeTripEndDate}
                        onAddItem={onAddItem}
                        trip={trip}
                        onOpenIdeas={onOpenIdeas}
                        tripDestination={trip.destination}
                        tripDestinations={trip.destinations}
                        dailyWeather={dailyWeather}
                        dailyTimezone={dailyTimezone}
                        dailyAlerts={dailyWeatherAlerts}
                        onOpenDayWeather={(dateKey) => {
                          setWeatherSheetDateKey(dateKey);
                          setWeatherSheetVisible(true);
                        }}
                        onResolveConflict={
                          onEditItem
                            ? (conflictItem) => onEditItem(conflictItem)
                            : undefined
                        }
                        tripId={trip.id}
                        isTripActive={isTripActive}
                        onOpenMobility={onOpenMobility}
                      />
                    }
                  >
                    <CalendarItineraryView
                      key={trip.id}
                      items={safeItems}
                      bookings={bookings}
                      startDate={safeTripStartDate}
                      endDate={safeTripEndDate}
                      focusedDateKey={focusedDateKey}
                      preferredTravelMode={trip.preferredTravelMode}
                      onEditItem={onEditItem}
                      onDeleteItem={onDeleteItem}
                      onViewDocument={onViewDocument}
                      canEdit={canEdit}
                      onAddItem={onAddItem}
                      onOpenIdeas={onOpenIdeas}
                      tripDestination={trip.destination}
                      tripDestinations={trip.destinations}
                      dailyWeather={dailyWeather}
                      dailyTimezone={dailyTimezone}
                      dailyAlerts={dailyWeatherAlerts}
                      onOpenDayWeather={(dateKey) => {
                        setWeatherSheetDateKey(dateKey);
                        setWeatherSheetVisible(true);
                      }}
                      onResolveConflict={
                        onEditItem
                          ? (conflictItem) => onEditItem(conflictItem)
                          : undefined
                      }
                      trip={trip}
                      isTripActive={isTripActive}
                      onOpenMobility={onOpenMobility}
                    />
                  </ItineraryViewErrorBoundary>
                ) : (
                  <ItineraryViewErrorBoundary
                    onError={() => {
                      // Already in list mode — surface the fallback UI in place.
                    }}
                    fallback={
                      <Card variant="outlined">
                        <Body>
                          Something went wrong rendering your itinerary list. Please refresh the screen.
                        </Body>
                      </Card>
                    }
                  >
                    <VerticalFlowView
                      items={safeItems}
                      bookings={bookings}
                      preferredTravelMode={trip.preferredTravelMode}
                      onEditItem={onEditItem}
                      onDeleteItem={onDeleteItem}
                      onViewDocument={onViewDocument}
                      canEdit={canEdit}
                      startDate={safeTripStartDate}
                      endDate={safeTripEndDate}
                      onAddItem={onAddItem}
                      trip={trip}
                      onOpenIdeas={onOpenIdeas}
                      tripDestination={trip.destination}
                      tripDestinations={trip.destinations}
                      dailyWeather={dailyWeather}
                      dailyTimezone={dailyTimezone}
                      dailyAlerts={dailyWeatherAlerts}
                      onOpenDayWeather={(dateKey) => {
                        setWeatherSheetDateKey(dateKey);
                        setWeatherSheetVisible(true);
                      }}
                      onResolveConflict={
                        onEditItem
                          ? (conflictItem) => onEditItem(conflictItem)
                          : undefined
                      }
                      tripId={trip.id}
                      isTripActive={isTripActive}
                      onOpenMobility={onOpenMobility}
                    />
                  </ItineraryViewErrorBoundary>
                )}
              </>
            )}
          </View>
        </Card>
      </View>
      <DayWeatherSheet
        visible={weatherSheetVisible}
        onClose={() => {
          setWeatherSheetVisible(false);
          setWeatherSheetDateKey(null);
        }}
        date={
          weatherSheetDateKey
            ? fromLocalDateKey(weatherSheetDateKey)
            : new Date()
        }
        weather={selectedDayWeather}
      />

      {Platform.OS === "web" && (
        <Modal
          visible={webAddChoiceVisible}
          transparent
          animationType="fade"
          onRequestClose={() => setWebAddChoiceVisible(false)}
        >
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Dismiss add options"
            onPress={() => setWebAddChoiceVisible(false)}
            style={{
              flex: 1,
              backgroundColor: theme.colors.overlay,
              justifyContent: "center",
              padding: theme.spacing(2),
            }}
          >
            <Pressable
              onPress={(e) => e.stopPropagation()}
              style={{
                backgroundColor: theme.colors.surface,
                borderRadius: theme.radii.xl,
                padding: theme.spacing(2),
                gap: theme.spacing(1.5),
                maxWidth: 400,
                width: "100%",
                alignSelf: "center",
              }}
            >
              <SubHeading style={{ fontSize: 18 }}>Add to itinerary</SubHeading>
              <Body color={theme.colors.textSecondary} style={{ fontSize: 14 }}>
                Choose how you want to add items.
              </Body>
              <View style={{ gap: theme.spacing(1) }}>
                <Button
                  label="Add manually"
                  variant="primary"
                  onPress={() => {
                    setWebAddChoiceVisible(false);
                    onAddItem?.();
                  }}
                />
                <Button
                  label="Import & connect"
                  variant="secondary"
                  onPress={() => {
                    setWebAddChoiceVisible(false);
                    onOpenImport?.();
                  }}
                />
                <Button
                  label="Cancel"
                  variant="ghost"
                  onPress={() => setWebAddChoiceVisible(false)}
                />
              </View>
            </Pressable>
          </Pressable>
        </Modal>
      )}
    </>
  );
}
